import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Card, 
  Page, 
  Layout, 
  Text,
  Button,
  Select,
  ProgressBar,
  Badge,
  Toast,
  Box,
  Icon,
  InlineGrid,
  InlineStack,
  BlockStack,
  Divider
} from '@shopify/polaris';
import {
  ClipboardMinor,
  PaperCheckMinor as CheckMinor,
  InfoMinor,
  AlertMinor,
  EmailMajor,
  LanguageMinor,
  TemplateMajor,
  SettingsMajor,
  CircleTickMajor
} from '@shopify/polaris-icons';
import { TitleBar } from '@shopify/app-bridge-react';

export default function EmailTemplates() {
  const { t } = useTranslation();
  
  // Check if App Bridge is available and properly initialized
  const isAppBridgeAvailable = () => {
    return typeof window !== 'undefined' && 
           window.appBridge &&
           window.getSessionToken;
  };

  const [appBridgeReady, setAppBridgeReady] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [templateCopied, setTemplateCopied] = useState(false);
  const [emailTemplate, setEmailTemplate] = useState('');
  const [templateLoading, setTemplateLoading] = useState(false);
  const [templateError, setTemplateError] = useState(null);
  const [templateType, setTemplateType] = useState('shipping');
  const [language, setLanguage] = useState('en');
  const [toastActive, setToastActive] = useState(false);

  const totalSteps = 5;

  // Monitor App Bridge availability
  useEffect(() => {
    const checkAppBridge = () => {
      const isReady = isAppBridgeAvailable();
      setAppBridgeReady(isReady);
    };

    // Check immediately
    checkAppBridge();

    // Check periodically until App Bridge is ready
    const interval = setInterval(() => {
      if (!appBridgeReady) {
        checkAppBridge();
      } else {
        clearInterval(interval);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [appBridgeReady]);

  const templateOptions = [
    { label: `📦 ${t('templateTypes.shipping')}`, value: 'shipping' },
    { label: `🛍️ ${t('templateTypes.order')}`, value: 'order' },
    { label: `🔄 ${t('templateTypes.shipping_update')}`, value: 'shipping_update' }
  ];

  const languageOptions = [
    { label: `🇺🇸 ${t('languages.en')}`, value: 'en' },
    { label: `🇧🇷 ${t('languages.pt')}`, value: 'pt' },
    { label: `🇩🇪 ${t('languages.de')}`, value: 'de' },
    { label: `🇪🇸 ${t('languages.es')}`, value: 'es' },
    { label: `🇳🇱 ${t('languages.nl')}`, value: 'nl' }
  ];

  // Load template dynamically
  const loadTemplate = async () => {
    setTemplateLoading(true);
    setTemplateError(null);
    
    try {
      const token = window.getSessionToken ? await window.getSessionToken() : null;
      const response = await fetch(`/api/templates/${templateType}/${language}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to load template: ${response.status}`);
      }
      
      const data = await response.json();
      setEmailTemplate(data.template);
    } catch (error) {
      console.error('Error loading template:', error);
      setTemplateError(t('emailTemplates.errorLoading'));
    } finally {
      setTemplateLoading(false);
    }
  };

  // Reload template when type or language changes
  useEffect(() => {
    if (emailTemplate || templateLoading) {
      setEmailTemplate('');
      loadTemplate();
    }
  }, [templateType, language]);

  const copyTemplate = async () => {
    if (!emailTemplate) {
      await loadTemplate();
    }

    if (emailTemplate) {
      try {
        await navigator.clipboard.writeText(emailTemplate);
        setTemplateCopied(true);
        setToastActive(true);
        setTimeout(() => {
          setTemplateCopied(false);
          setToastActive(false);
        }, 3000);
      } catch (error) {
        console.error('Error copying template:', error);
        setTemplateError(t('emailTemplates.errorCopying'));
      }
    }
  };

  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const getStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <Card>
            <Box padding="400">
              <BlockStack gap="300">
                <Text variant="bodyMd" as="p">1. {t('emailTemplates.shopifyAdmin')} <strong>{t('emailTemplates.settings')}</strong> (Settings) {t('emailTemplates.sidebarBottom')}</Text>
                <Text variant="bodyMd" as="p">2. {t('emailTemplates.clickNotifications')} <strong>{t('emailTemplates.notifications')}</strong> (Notifications) {t('emailTemplates.settingsList')}</Text>
              </BlockStack>
            </Box>
          </Card>
        );
      case 2:
        return (
          <Card>
            <Box padding="400">
              <BlockStack gap="300">
                <Text variant="bodyMd" as="p">3. Na seção <strong>Cliente</strong>, procure pelo email:</Text>
                {templateType === 'shipping' && (
                  <Text variant="bodyMd" as="p"><strong>"{t('emailTemplates.shippingConfirmation')}"</strong> ou <strong>"Shipping confirmation"</strong></Text>
                )}
                {templateType === 'order' && (
                  <Text variant="bodyMd" as="p"><strong>"{t('emailTemplates.orderConfirmation')}"</strong> ou <strong>"Order confirmation"</strong></Text>
                )}
                {templateType === 'shipping_update' && (
                  <Text variant="bodyMd" as="p"><strong>"{t('emailTemplates.shippingUpdate')}"</strong> ou <strong>"Shipping update"</strong></Text>
                )}
                <Text variant="bodyMd" as="p">4. Clique no botão <strong>{t('emailTemplates.editCode')}</strong> ao lado do email.</Text>
              </BlockStack>
            </Box>
          </Card>
        );
      case 3:
        return (
          <Card>
            <Box padding="400">
              <BlockStack gap="300">
                <Text variant="bodyMd" as="p">5. Na tela de edição do template, você verá várias abas. Clique na aba <strong>HTML</strong>.</Text>
                <Text variant="bodyMd" as="p">6. Selecione todo o código HTML existente (Ctrl+A ou Cmd+A) e delete tudo.</Text>
              </BlockStack>
            </Box>
          </Card>
        );
      case 4:
        return (
          <Card>
            <Box padding="400">
              <BlockStack gap="300">
                <Text variant="bodyMd" as="p">7. Cole o novo template HTML que você copiou na seção anterior.</Text>
                <Text variant="bodyMd" as="p">8. Clique em <strong>Salvar</strong> para aplicar as alterações.</Text>
              </BlockStack>
            </Box>
          </Card>
        );
      case 5:
        return (
          <Card>
            <Box padding="400">
              <BlockStack gap="400">
                <BlockStack gap="300">
                  <Text variant="bodyMd" as="p">9. Clique em <strong>Visualizar</strong> para ver como o email ficará.</Text>
                  <Text variant="bodyMd" as="p">10. Se estiver tudo correto, clique em <strong>Salvar</strong> para finalizar.</Text>
                </BlockStack>
                <div style={{
                  backgroundColor: '#f0fdf4',
                  border: '1px solid #22c55e',
                  borderRadius: '6px',
                  padding: '16px'
                }}>
                  <Text variant="bodyMd" as="p" style={{ color: '#166534', margin: '0' }}>🎉 Perfeito! Agora seus clientes receberão emails com informações de rastreamento do TrackingMania!</Text>
                </div>
              </BlockStack>
            </Box>
          </Card>
        );
      default:
        return null;
    }
  };

  const toastMarkup = toastActive ? (
    <Toast content={t('emailTemplates.templateCopied')} onDismiss={() => setToastActive(false)} />
  ) : null;

  return (
    <>
      {appBridgeReady && (
        <TitleBar title={t('emailTemplates.title')} />
      )}
      <Page
        title={t('emailTemplates.title')}
        subtitle={t('emailTemplates.subtitle')}
        breadcrumbs={[{ content: t('navigation.dashboard'), url: '/' }]}
      >
        <Layout>
          {/* Template Settings - Modern Design */}
          <Layout.Section>
            <Card padding="400">
              <BlockStack gap="400">
                <InlineStack gap="300" align="start">
                  <div style={{ 
                    backgroundColor: '#e0f5ff', 
                    padding: '12px', 
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Icon source={SettingsMajor} color="primary" />
                  </div>
                  <BlockStack gap="200">
                    <Text variant="headingMd" as="h3">
                      {t('emailTemplates.templateSettings')}
                    </Text>
                    <Text variant="bodyMd" as="p" color="subdued">
                      Choose your template type and language preferences
                    </Text>
                  </BlockStack>
                </InlineStack>
                
                <Divider />
                
                <InlineGrid columns={{ xs: '1', sm: '2' }} gap="400">
                  <Select
                    label={t('emailTemplates.templateType')}
                    options={templateOptions}
                    value={templateType}
                    onChange={setTemplateType}
                  />
                  <Select
                    label={t('common.language')}
                    options={languageOptions}
                    value={language}
                    onChange={setLanguage}
                  />
                </InlineGrid>
              </BlockStack>
            </Card>
          </Layout.Section>

          {/* Why change template explanation */}
          <Layout.Section>
            <Card>
              <Box padding="400">
                <InlineStack gap="300" align="start">
                  <div style={{ 
                    backgroundColor: '#fff7ed', 
                    padding: '12px', 
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Icon source={InfoMinor} color="warning" />
                  </div>
                  <BlockStack gap="200">
                    <Text variant="headingMd" as="h3">
                      🤔 {t('emailTemplates.whyChange')}
                    </Text>
                  </BlockStack>
                </InlineStack>
              </Box>
              <Divider />
              <Box padding="400">
                {templateType === 'shipping' ? (
                  <BlockStack gap="400">
                    <Text variant="bodyMd" as="p"><strong>{t('emailTemplates.problem')}</strong></Text>
                    <InlineGrid columns={{ xs: '1', sm: '2' }} gap="400">
                      <div style={{
                        backgroundColor: '#fffbeb',
                        border: '1px solid #f59e0b',
                        borderRadius: '6px',
                        padding: '16px'
                      }}>
                        <Text variant="headingSm" as="h4" style={{ color: '#92400e', marginBottom: '8px' }}>{`❌ ${t('emailTemplates.defaultTemplate')}`}</Text>
                        <Text variant="bodyMd" as="p" style={{ color: '#92400e', margin: '0' }}>{t('emailTemplates.showsCarrierNumber')}</Text>
                      </div>
                      <div style={{
                        backgroundColor: '#f0fdf4',
                        border: '1px solid #22c55e',
                        borderRadius: '6px',
                        padding: '16px'
                      }}>
                        <Text variant="headingSm" as="h4" style={{ color: '#166534', marginBottom: '8px' }}>{`✅ ${t('emailTemplates.ourTemplate')}`}</Text>
                        <Text variant="bodyMd" as="p" style={{ color: '#166534', margin: '0' }}>{t('emailTemplates.showsTrackingManiaInfo')}</Text>
                      </div>
                    </InlineGrid>
                    <Text variant="bodyMd" as="p"><strong>A solução:</strong> {t('emailTemplates.solution')}</Text>
                  </BlockStack>
                ) : (
                  <BlockStack gap="400">
                    <Text variant="bodyMd" as="p"><strong>O problema:</strong> O template padrão de confirmação de pedido do Shopify não inclui informações sobre o TrackingMania. Seus clientes precisam saber desde o primeiro momento que receberão emails de rastreamento personalizados.</Text>
                    <InlineGrid columns={{ xs: '1', sm: '2' }} gap="400">
                      <div style={{
                        backgroundColor: '#fffbeb',
                        border: '1px solid #f59e0b',
                        borderRadius: '6px',
                        padding: '16px'
                      }}>
                        <Text variant="headingSm" as="h4" style={{ color: '#92400e', marginBottom: '8px' }}>{`❌ ${t('emailTemplates.defaultTemplate')}`}</Text>
                        <Text variant="bodyMd" as="p" style={{ color: '#92400e', margin: '0' }}>Não menciona o sistema de rastreamento TrackingMania</Text>
                      </div>
                      <div style={{
                        backgroundColor: '#f0fdf4',
                        border: '1px solid #22c55e',
                        borderRadius: '6px',
                        padding: '16px'
                      }}>
                        <Text variant="headingSm" as="h4" style={{ color: '#166534', marginBottom: '8px' }}>{`✅ ${t('emailTemplates.ourTemplate')}`}</Text>
                        <Text variant="bodyMd" as="p" style={{ color: '#166534', margin: '0' }}>Informa sobre emails diários do TrackingMania desde o primeiro contato</Text>
                      </div>
                    </InlineGrid>
                    <Text variant="bodyMd" as="p"><strong>A solução:</strong> Substituir o template padrão pelo nosso template personalizado, que já informa o cliente sobre o sistema TrackingMania desde a confirmação do pedido.</Text>
                  </BlockStack>
                )}
              </Box>
            </Card>
          </Layout.Section>

          {/* Copy Template Section */}
          <Layout.Section>
            <Card>
              <Box padding="400">
                <InlineStack gap="300" align="start">
                  <div style={{ 
                    backgroundColor: '#f0f9ff', 
                    padding: '12px', 
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Icon source={TemplateMajor} color="primary" />
                  </div>
                  <BlockStack gap="200">
                    <Text variant="headingMd" as="h3">
                      📋 1. {t('emailTemplates.copyTemplate')}
                    </Text>
                  </BlockStack>
                </InlineStack>
              </Box>
              <Divider />
              <Box padding="400">
                <BlockStack gap="400">
                  <Text variant="bodyMd" as="p">
                    {templateType === 'shipping' && `${t('emailTemplates.templateForShipping')} ${language === 'en' ? 'Inglês' : language === 'pt' ? 'Português' : language === 'de' ? 'Alemão' : language === 'es' ? 'Espanhol' : 'Holandês'}.`}
                    {templateType === 'order' && `Template para confirmação de pedido em ${language === 'en' ? 'Inglês' : language === 'pt' ? 'Português' : language === 'de' ? 'Alemão' : language === 'es' ? 'Espanhol' : 'Holandês'}.`}
                    {templateType === 'shipping_update' && `Template para atualização de envio em ${language === 'en' ? 'Inglês' : language === 'pt' ? 'Português' : language === 'de' ? 'Alemão' : language === 'es' ? 'Espanhol' : 'Holandês'}.`}
                  </Text>

                  {/* Template Display */}
                  <div style={{ position: 'relative' }}>
                    {templateLoading ? (
                      <div style={{ 
                        backgroundColor: '#1a1a1a', 
                        color: '#ffffff', 
                        padding: '20px', 
                        borderRadius: '8px',
                        minHeight: '200px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <BlockStack gap="200" align="center">
                          <Text variant="bodyMd" as="p">{t('emailTemplates.loadingTemplate')}</Text>
                        </BlockStack>
                      </div>
                    ) : templateError ? (
                      <div style={{
                        backgroundColor: '#fef2f2',
                        border: '1px solid #ef4444',
                        borderRadius: '6px',
                        padding: '16px'
                      }}>
                        <p style={{ color: '#dc2626', margin: '0 0 12px 0' }}>{templateError}</p>
                        <Button onClick={loadTemplate}>{t('emailTemplates.tryAgain')}</Button>
                      </div>
                    ) : emailTemplate ? (
                      <div style={{ 
                        backgroundColor: '#1a1a1a', 
                        color: '#ffffff', 
                        padding: '20px', 
                        borderRadius: '8px',
                        maxHeight: '300px',
                        overflow: 'auto',
                        fontFamily: 'monospace',
                        fontSize: '14px',
                        lineHeight: '1.5'
                      }}>
                        <pre style={{ margin: 0 }}>{emailTemplate}</pre>
                        <div style={{ position: 'absolute', top: '10px', right: '10px' }}>
                          <Button
                            onClick={copyTemplate}
                            disabled={templateLoading}
                            primary={!templateCopied}
                            outline={templateCopied}
                            icon={templateCopied ? CheckMinor : ClipboardMinor}
                          >
                            {templateCopied ? t('common.copied') : t('common.copy')}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ 
                        backgroundColor: '#1a1a1a', 
                        color: '#ffffff', 
                        padding: '20px', 
                        borderRadius: '8px',
                        minHeight: '200px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <BlockStack gap="400" align="center">
                          <Text variant="bodyMd" as="p">{t('emailTemplates.templateReady')}</Text>
                          <Button onClick={loadTemplate} variant="primary" size="large">
                            📄 {t('emailTemplates.loadTemplate')}
                          </Button>
                        </BlockStack>
                      </div>
                    )}
                  </div>

                  {templateCopied && (
                    <div style={{
                      backgroundColor: '#f0fdf4',
                      border: '1px solid #22c55e',
                      borderRadius: '6px',
                      padding: '16px'
                    }}>
                      <p style={{ color: '#166534', margin: '0' }}>✅ {t('emailTemplates.templateCopied')} {t('emailTemplates.applyInShopify')}</p>
                    </div>
                  )}
                </BlockStack>
              </Box>
            </Card>
          </Layout.Section>

          {/* Tutorial Steps */}
          <Layout.Section>
            <Card>
              <Box padding="400">
                <InlineStack gap="300" align="start">
                  <div style={{ 
                    backgroundColor: '#f0fdf4', 
                    padding: '12px', 
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Icon source={SettingsMajor} color="success" />
                  </div>
                  <BlockStack gap="200">
                    <Text variant="headingMd" as="h3">
                      ⚙️ 2. Como Aplicar o Template no Shopify ({templateType === 'shipping' ? 'Shipping confirmation' : templateType === 'order' ? 'Order confirmation' : 'Shipping update'})
                    </Text>
                  </BlockStack>
                </InlineStack>
              </Box>
              <Divider />
              <Box padding="400">
                <BlockStack gap="400">
                  <InlineStack gap="300" align="center">
                    <Badge>{`${t('emailTemplates.step')} ${currentStep}/${totalSteps}`}</Badge>
                    <InlineStack gap="200">
                      <Button onClick={prevStep} disabled={currentStep === 1}>
                        {t('common.back')}
                      </Button>
                      <Button onClick={nextStep} variant="primary" disabled={currentStep === totalSteps}>
                        {t('common.next')}
                      </Button>
                    </InlineStack>
                  </InlineStack>
                  <ProgressBar progress={(currentStep / totalSteps) * 100} />
                  {getStepContent()}
                </BlockStack>
              </Box>
            </Card>
          </Layout.Section>

          {/* Benefits */}
          <Layout.Section>
            <Card>
              <Box padding="400">
                <InlineStack gap="300" align="start">
                  <div style={{ 
                    backgroundColor: '#e0f5ff', 
                    padding: '12px', 
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Icon source={CircleTickMajor} color="highlight" />
                  </div>
                  <BlockStack gap="200">
                    <Text variant="headingMd" as="h3">
                      🚀 {t('emailTemplates.benefits')}
                    </Text>
                  </BlockStack>
                </InlineStack>
              </Box>
              <Divider />
              <Box padding="400">
                <InlineGrid columns={{ xs: '1', sm: '2' }} gap="400">
                  <BlockStack gap="200">
                    <Text as="h4" variant="headingSm">{t('emailTemplates.consistentTracking')}</Text>
                    <Text variant="bodyMd" as="p" color="subdued">{t('emailTemplates.consistentTrackingDesc')}</Text>
                  </BlockStack>
                  <BlockStack gap="200">
                    <Text as="h4" variant="headingSm">{t('emailTemplates.unifiedExperience')}</Text>
                    <Text variant="bodyMd" as="p" color="subdued">{t('emailTemplates.unifiedExperienceDesc')}</Text>
                  </BlockStack>
                  <BlockStack gap="200">
                    <Text as="h4" variant="headingSm">{t('emailTemplates.lessConfusion')}</Text>
                    <Text variant="bodyMd" as="p" color="subdued">{t('emailTemplates.lessConfusionDesc')}</Text>
                  </BlockStack>
                  <BlockStack gap="200">
                    <Text as="h4" variant="headingSm">{t('emailTemplates.trackingManiaBrand')}</Text>
                    <Text variant="bodyMd" as="p" color="subdued">{t('emailTemplates.trackingManiaBrandDesc')}</Text>
                  </BlockStack>
                </InlineGrid>
              </Box>
            </Card>
          </Layout.Section>

          {/* Tracking Page Configuration */}
          <Layout.Section>
            <Card>
              <Box padding="400">
                <InlineStack gap="300" align="start">
                  <div style={{ 
                    backgroundColor: '#f0f9ff', 
                    padding: '12px', 
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Icon source={EmailMajor} color="primary" />
                  </div>
                  <BlockStack gap="200">
                    <Text variant="headingMd" as="h3">
                      🔗 {t('emailTemplates.trackingPageConfig')}
                    </Text>
                  </BlockStack>
                </InlineStack>
              </Box>
              <Divider />
              <Box padding="400">
                <BlockStack gap="400">
                  <Text variant="bodyMd" as="p">{t('emailTemplates.additionalConfig')}</Text>
                  
                  <div style={{
                    backgroundColor: '#f0f9ff',
                    border: '1px solid #3b82f6',
                    borderRadius: '6px',
                    padding: '16px'
                  }}>
                    <Text variant="headingSm" as="h4" style={{ color: '#1e40af', marginBottom: '12px' }}>{`✨ ${t('emailTemplates.howToConfigureSimple')}`}</Text>
                    <BlockStack gap="200">
                      <Text variant="bodyMd" as="p" style={{ color: '#1e40af', margin: '0' }}><strong>1.</strong> {t('emailTemplates.goToPersonalization')}</Text>
                      <Text variant="bodyMd" as="p" style={{ color: '#1e40af', margin: '0' }}><strong>2.</strong> {t('emailTemplates.findTrackingSection')}</Text>
                      <Text variant="bodyMd" as="p" style={{ color: '#1e40af', margin: '0' }}><strong>3.</strong> {t('emailTemplates.releaseScopes')}</Text>
                      <Text variant="bodyMd" as="p" style={{ color: '#1e40af', margin: '0' }}><strong>4.</strong> {t('emailTemplates.clickCreatePage')}</Text>
                      <Text variant="bodyMd" as="p" style={{ color: '#1e40af', margin: '0' }}><strong>5.</strong> {t('emailTemplates.addToMenu')}</Text>
                    </BlockStack>
                  </div>

                  <InlineGrid columns={{ xs: '1', sm: '2' }} gap="400">
                    <BlockStack gap="200">
                      <InlineStack gap="200" align="start">
                        <span style={{ fontSize: '24px' }}>🎯</span>
                        <Text as="h4" variant="headingSm">{t('emailTemplates.directAccess')}</Text>
                      </InlineStack>
                      <Text variant="bodyMd" as="p" color="subdued">{t('emailTemplates.directAccessDesc')}</Text>
                    </BlockStack>
                    <BlockStack gap="200">
                      <InlineStack gap="200" align="start">
                        <span style={{ fontSize: '24px' }}>🎯</span>
                        <Text as="h4" variant="headingSm">{t('emailTemplates.integratedExperience')}</Text>
                      </InlineStack>
                      <Text variant="bodyMd" as="p" color="subdued">{t('emailTemplates.integratedExperienceDesc')}</Text>
                    </BlockStack>
                  </InlineGrid>

                  <div style={{
                    backgroundColor: '#f0fdf4',
                    border: '1px solid #22c55e',
                    borderRadius: '6px',
                    padding: '16px'
                  }}>
                    <BlockStack gap="200">
                      <Text as="h4" variant="headingSm" style={{ color: '#166534' }}>{t('emailTemplates.importantTip')}</Text>
                      <Text variant="bodyMd" as="p" style={{ color: '#166534', margin: '0' }}>{t('emailTemplates.importantTipDesc')}</Text>
                    </BlockStack>
                  </div>
                </BlockStack>
              </Box>
            </Card>
          </Layout.Section>

          {/* Important Warning */}
          <Layout.Section>
            <div style={{
              backgroundColor: '#fffbeb',
              border: '1px solid #f59e0b',
              borderRadius: '6px',
              padding: '16px'
            }}>
              <BlockStack gap="200">
                <Text as="h3" variant="headingMd" style={{ color: '#92400e' }}>{t('emailTemplates.makeBackupTitle')}</Text>
                <Text variant="bodyMd" as="p" style={{ color: '#92400e', margin: '0' }}>{t('emailTemplates.makeBackupDesc')}</Text>
              </BlockStack>
            </div>
          </Layout.Section>
        </Layout>
        {toastMarkup}
      </Page>
    </>
  );
}